import React from 'react';

import { KebabHorizontalIcon } from '@primer/octicons-react';
import { Tooltip as PrimerTooltip } from '@primer/react';

import isFunction from 'lodash/isFunction';

import classNames from 'classnames';

import useChartData from '../ChartContext';

import BasicDropdown from '../../common/BasicDropdown/BasicDropdown';
import Button from '../../common/Button/Button';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import Icon from '../../common/icons/Icon';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';

import ChartEmptyState from '../ChartEmptyState';
import ChartLegend from './legend/D3ChartLegend';

import { getThemeColor } from '../../../utils/theme-utils';

const ChartDropdown = ({ isDisabled }) => {
  const { dropdownOptions } = useChartData();

  if (!dropdownOptions?.length) {
    return null;
  }
  return (
    <BasicDropdown
      direction="top-left"
      dropdownLabel={null}
      iconFill="brand"
      iconSize={12}
      icon={KebabHorizontalIcon}
      isDisabled={isDisabled}
      values={dropdownOptions}
      hideCaret
      hideBorder
    />
  )
}

const IconChartMenu = () => {
  const { dropdownOptions } = useChartData();

  return (
    <div className="flex">
      {
        dropdownOptions.map(({
          id, icon, label, onClick, useTailwindSizing = false
        }) => (
          <Button
            key={id}
            id={id}
            className="m-1"
            variant="invisible"
            onClick={onClick}
          >
            <PrimerTooltip
              aria-label={label}
            >
              <Icon
                fill={getThemeColor('brand')}
                icon={icon}
                useTailwindSizing={useTailwindSizing}
                size={24}
              />
            </PrimerTooltip>
          </Button>
        ))
      }
    </div>
  )
}

const NoDataOverlay = ({
  chartID,
  chartHidden,
  isLoading,
  loadingText,
  message,
}) => {
  if (!chartHidden) {
    return null;
  }
  
  return (
    <div className="relative h-full flex-grow flex flex-col justify-center items-center">
      {
        isLoading ? <LoadingSpinner loadingText={loadingText} animationKey={`${chartID}-spinner`} useOverlay /> : <ChartEmptyState message={message} />
      }
    </div>
  )
}

const ChartOptions = ({
  showCloseButton,
  showIconBasedMenu,
  noChartDropdown,
  message,
  onClose,
}) => (
  <div className="flex justify-end items-center">
    {
      showIconBasedMenu && <IconChartMenu />
    }
    {
      noChartDropdown ? null : <ChartDropdown isDisabled={!!message} />
    }
    {
      showCloseButton && (
        <Button
          variant="close"
          textColor="border-dark"
          hoverTextColor="border-darker"
          activeTextColor="border-darkest"
          onClick={onClose}
        />
      )
    }
  </div>
)

export const ChartWrapper = ({
  chartId,
  className,
  chartTitle,
  message,
  isLoading,
  detailRenderer,
  children,
  containerRef,
  legendEntries,
  cardStyle = { height: '400px' },
  containerHeight = 212,
  onClose = undefined,
  disableFullHeight = false,
  showCloseButton = false,
  hideDropdownMenu = false,
  showIconBasedMenu = false,
  showLegend = false,
  loadingText = 'Loading...',
}) => {
  const { chartID, noDataAvailable } = useChartData();
  const refObj = containerRef ? { ref: containerRef } : {};

  const noChartDropdown = (hideDropdownMenu || showIconBasedMenu);
  const chartHidden = message || noDataAvailable;
  return (
    <CardView 
      className={className} 
      disableFullHeight={disableFullHeight}
      cardStyle={cardStyle}
    >
      <div className="flex flex-row justify-between items-center">
        <CardHeader>{chartTitle}</CardHeader>
        <ChartOptions
          message={message}
          noChartDropdown={noChartDropdown}
          showCloseButton={showCloseButton}
          showIconBasedMenu={showIconBasedMenu}
          onClose={onClose}
        />
      </div>
      {
        showLegend && !noDataAvailable && (
          <ChartLegend id={`${chartId}-legend`} entries={legendEntries} />
        )
      }
      <div
        className={classNames(
          'relative flex-grow min-h-max w-full',
          { hidden: chartHidden }
        )}
        style={{ height: containerHeight }}
        {...refObj}
      >        {
          isLoading && <LoadingSpinner loadingText={loadingText} animationKey={`${chartID}-spinner`} useOverlay />
        }
        {
          children
        }
      </div>
      <NoDataOverlay 
        chartID={chartID}
        chartHidden={chartHidden}
        isLoading={isLoading}
        loadingText={loadingText}
        message={message}
      />
      {
        isFunction(detailRenderer) ? detailRenderer() : null
      }
    </CardView>
  );
};
export default ChartWrapper;
